import './TimeFrameSelector.scss';
import React, { useState, useMemo, useEffect } from 'react';
import { coerceEpoch, epochToDateTimeString } from 'helpers/common.helper';
import { DATE_FORMAT_OPTIONS } from 'pages/AnalyticsV2/Overview/constants';
import { FlightDateRangePicker, FlightSelect } from '@flybits/design-system';
import { AdvancedDateRange, ChartConfigState, INTERVAL_OPTIONS } from 'pages/AnalyticsV2/types';
import { TIMEFRAME_INTERVAL_OPTIONS } from 'pages/AnalyticsV2/CustomAnalytics/CreateOrEditChart/constants';

const MAIN_CLASS = 'timeframe-selector';
const CLASSES = {
  LABEL: `${MAIN_CLASS}__label`,
  BLOCK: `${MAIN_CLASS}__block`,
  PREVIEW: `${MAIN_CLASS}__preview`,
  DATE_RANGE: `${MAIN_CLASS}__date-range`,
  DATE_RANGE_DD: `${MAIN_CLASS}__date-range__dropdown`,
};

interface TimeFrameSelectorProps {
  chartConfig: ChartConfigState;
  onChange: (timeFrame: ChartConfigState['timeFrame']) => void;
  onValidation: (isValid: boolean) => void;
  hiddenIntervals?: INTERVAL_OPTIONS[];
}

export default function TimeFrameSelector({
  chartConfig,
  onChange,
  onValidation,
  hiddenIntervals,
}: TimeFrameSelectorProps) {
  const { start, end, period, relative } = chartConfig.timeFrame || {};

  const initialDateRange = useMemo<AdvancedDateRange>(() => {
    if (start && end) {
      return [start, end];
    } else if (relative && relative[0] >= 0 && !!relative[1]) {
      // right now 'Today' is serialized with relatvieTimeOffset as 1 ... but the design system component expect 0
      return [null, null, relative[0] === 1 ? 0 : relative[0], relative[1]];
    } else {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const endOfToday = new Date();
      endOfToday.setHours(23, 59, 59, 999);

      return [startOfToday, endOfToday, 7, INTERVAL_OPTIONS.DAILY];
    }
  }, [start, end, relative]);

  const [selectedDateRange, setSelectedDateRange] = useState<AdvancedDateRange>();

  const intervalOptions = useMemo(
    () =>
      TIMEFRAME_INTERVAL_OPTIONS.map((interval) => {
        const { label, value, duration } = interval;
        return { key: value, name: label, duration };
      }).filter((opt) => {
        if (opt.key && hiddenIntervals?.includes(opt.key as INTERVAL_OPTIONS)) {
          return false;
        }
        const dateRange = selectedDateRange || initialDateRange;
        const [start, end, relativeTimeOffset, relativeTimeUnit] = dateRange;

        if (relativeTimeOffset === 0) {
          return opt.key === INTERVAL_OPTIONS.HOURLY;
        } else if (start && end) {
          const duration = coerceEpoch(end) - coerceEpoch(start);
          return duration > opt.duration;
        } else if (relativeTimeOffset && relativeTimeUnit) {
          const intervalOption =
            TIMEFRAME_INTERVAL_OPTIONS.find((opt) => opt.value === relativeTimeUnit) || TIMEFRAME_INTERVAL_OPTIONS[0];
          return relativeTimeOffset * intervalOption.duration > opt.duration;
        }
      }),
    [selectedDateRange, initialDateRange, hiddenIntervals],
  );

  const initialTimeInterval = useMemo(
    () =>
      period
        ? {
            key: period as INTERVAL_OPTIONS,
            name: TIMEFRAME_INTERVAL_OPTIONS.find((intervalOption) => intervalOption.value === period)?.label || '',
            duration: coerceEpoch(selectedDateRange?.[1] || 0) - coerceEpoch(selectedDateRange?.[0] || 0),
          }
        : intervalOptions[1],
    [period, selectedDateRange, intervalOptions],
  );

  const [selectedTimeInterval, setSelectedTimeInterval] = useState<(typeof intervalOptions)[number] | undefined>();

  const handleDateRangeChange = (selectedDate: AdvancedDateRange) => {
    setSelectedDateRange(selectedDate);

    const [start, end, relativeTimeOffset, relativeTimeUnit] = selectedDate;

    const { key, duration } = selectedTimeInterval || initialTimeInterval;

    let isValidInterval = true;
    if (start && end) {
      const newDuration = coerceEpoch(end) - coerceEpoch(start);

      isValidInterval = newDuration > (duration || 0);
      !isValidInterval && setSelectedTimeInterval(undefined);
    }

    onChange({
      start,
      end,
      period: isValidInterval ? key : undefined,
      relative: [relativeTimeOffset as number, relativeTimeUnit as string],
    });
  };

  const handleIntervalChange = ({ key, name, duration }: { key: INTERVAL_OPTIONS; name: string; duration: number }) => {
    setSelectedTimeInterval({
      key,
      name,
      duration,
    });

    const [start, end, relativeTimeOffset, relativeTimeUnit] = selectedDateRange || initialDateRange;

    onChange({
      start,
      end,
      period: key,
      relative: [relativeTimeOffset as number, relativeTimeUnit as string],
    });
  };

  const showPreview = () => {
    const dateRange = selectedDateRange || initialDateRange;
    const [start, end, relativeTimeOffset] = dateRange;

    const shouldShowPreview = start && end && relativeTimeOffset !== null && relativeTimeOffset !== undefined;

    return shouldShowPreview ? (
      <div className={CLASSES.PREVIEW}>
        {relativeTimeOffset === 0
          ? `${epochToDateTimeString(new Date().getTime(), 'en-US', DATE_FORMAT_OPTIONS)}`
          : `${epochToDateTimeString(start, 'en-US', DATE_FORMAT_OPTIONS)} - ${epochToDateTimeString(
              end,
              'en-US',
              DATE_FORMAT_OPTIONS,
            )}`}
      </div>
    ) : null;
  };

  useEffect(() => {
    const dateRange = selectedDateRange || initialDateRange;
    const timeInterval = selectedTimeInterval || initialTimeInterval;
    const isTimePeriodValid =
      relative?.[0] !== undefined && relative?.[0] !== null && relative?.[1]
        ? dateRange[2] !== undefined && dateRange[2] !== null && !!dateRange[3]
        : !!dateRange[0] && !!dateRange[1];
    const isTimeIntervalValid = !!timeInterval?.key;

    const isValid = isTimePeriodValid && isTimeIntervalValid;
    onValidation(isValid);
  }, [selectedDateRange, selectedTimeInterval, initialDateRange, initialTimeInterval, relative, onValidation]);

  return (
    <section className={MAIN_CLASS}>
      <label className={CLASSES.LABEL} htmlFor="select-period">
        Select a period of time
      </label>
      <div className={CLASSES.BLOCK}>
        <FlightDateRangePicker
          className={`${CLASSES.DATE_RANGE} half`}
          value={selectedDateRange || initialDateRange}
          onChange={handleDateRangeChange as React.ComponentProps<typeof FlightDateRangePicker>['onChange']}
          maxDate={new Date()}
          includeAllTime={false}
          includeToday
          isIncludeCustom
          replaceCustomRangeLabel={false}
          ariaControlsId={'select-period'}
        />
        {showPreview()}
      </div>
      <label className={CLASSES.LABEL} htmlFor="select-interval">
        Select an interval
      </label>
      <FlightSelect
        className={`${CLASSES.DATE_RANGE_DD}`}
        options={intervalOptions}
        selected={selectedTimeInterval || initialTimeInterval}
        ariaControlsId={'select-interval'}
        width={'100%'}
        handleOptionClick={handleIntervalChange}
      />
    </section>
  );
}
